import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Card, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  fetchProductBrands,
  createProductBrand,
  updateProductBrand,
  deleteProductBrand,
  type ProductBrandDto,
} from '../services/productBrandService';
import { useCanDelete } from '../hooks/useCanDelete';

const ProductBrands: React.FC = () => {
  const canDelete = useCanDelete();
  const [list, setList] = useState<ProductBrandDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<ProductBrandDto | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchProductBrands();
      setList(data);
    } catch (e) {
      message.error('Ürün markaları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: ProductBrandDto) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code ?? '',
      logoUrl: record.logoUrl ?? '',
      isFavourite: record.isFavourite,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await deleteProductBrand(id);
      message.success('Ürün markası silindi');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        await updateProductBrand(editing.id, {
          name: values.name,
          code: values.code || undefined,
          logoUrl: values.logoUrl || undefined,
          isFavourite: !!values.isFavourite,
        });
        message.success('Ürün markası güncellendi');
      } else {
        await createProductBrand({
          name: values.name,
          code: values.code || undefined,
          logoUrl: values.logoUrl || undefined,
          isFavourite: !!values.isFavourite,
        });
        message.success('Ürün markası eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'İşlem sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Ad', dataIndex: 'name', key: 'name' },
    { title: 'Kod', dataIndex: 'code', key: 'code', render: (v: string) => v || '—' },
    {
      title: 'Favori',
      dataIndex: 'isFavourite',
      key: 'isFavourite',
      render: (v: boolean) => (v ? 'Evet' : 'Hayır'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: ProductBrandDto) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu ürün markasını silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title="Ürün Markaları">
      <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
        Ürün markaları (Bridgestone, Pirelli, Bosch vb.) ürün eklerken seçilebilir. Araç markaları (Araç Yönetimi) ile ayrıdır.
      </p>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Ürün Markası
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={list}
        columns={columns}
        pagination={{ pageSize: 20 }}
      />
      <Modal
        title={editing ? 'Ürün Markası Düzenle' : 'Yeni Ürün Markası'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Marka Adı" rules={[{ required: true, message: 'Marka adı giriniz' }]}>
            <Input placeholder="Örn. Bridgestone" />
          </Form.Item>
          <Form.Item name="code" label="Kod">
            <Input placeholder="Opsiyonel kısa kod" />
          </Form.Item>
          <Form.Item name="logoUrl" label="Logo URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="isFavourite" label="Favori" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductBrands;
